import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Navbar } from "@/components/layout/Navbar";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Leaf, Mail, Lock, Eye, EyeOff, User, Building2, Users, ArrowRight, Check, Loader2, Phone, AlertTriangle } from "lucide-react";
import { cn } from "@/lib/utils";
import { useAuth } from "@/contexts/AuthContext";
import { useStaffAccess } from "@/hooks/useStaffAccess";
import { useToast } from "@/hooks/use-toast";
import { useRateLimit } from "@/hooks/useRateLimit";
import { z } from "zod";
import { SEOHead } from "@/components/seo/SEOHead";
import { TermsModal } from "@/components/terms/TermsModal";
import { PrivacyConsentModal } from "@/components/terms/PrivacyConsentModal";
import { supabase } from "@/integrations/supabase/client";
import { checkPasswordBreached } from "@/lib/passwordSecurity";
import { PasswordStrengthIndicator, isPasswordValid } from "@/components/auth/PasswordStrengthIndicator";

const emailSchema = z.string().email("Please enter a valid email address");
const passwordSchema = z.string().min(8, "Password must be at least 8 characters");

const userTypes = [
  {
    id: "customer",
    icon: User,
    title: "Customer",
    description: "I want to book wellness services",
    price: "Free forever",
  },
  {
    id: "solo",
    icon: Users,
    title: "Solo Practitioner",
    description: "I'm an individual wellness professional",
    price: "Free",
  },
  {
    id: "center_admin",
    icon: Building2,
    title: "Premium Business",
    description: "I manage a wellness center or team",
    price: "$9.99/month",
  },
];

export default function Auth() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const mode = searchParams.get("mode") || "login";
  const isLogin = mode === "login";
  
  // Check for invite token in URL (robust) or sessionStorage (fallback)
  const urlInviteToken = searchParams.get("inviteToken");
  const sessionInviteToken = sessionStorage.getItem("pendingInviteToken");
  const inviteToken = urlInviteToken || sessionInviteToken;
  
  // Check if this is a staff invitation signup - determined synchronously
  const isStaffInvite = searchParams.get("staffInvite") === "true" || !!inviteToken;
  
  // Staff invite email from sessionStorage
  const storedStaffEmail = sessionStorage.getItem("staffInviteEmail") || "";
  
  // Initialize step SYNCHRONOUSLY - staff invites skip directly to step 2 (no user-type chooser)
  const [step, setStep] = useState(() => isStaffInvite ? 2 : 1);
  // Staff members register as "customer" role - their access comes from business_staff table
  const [userType, setUserType] = useState(() => isStaffInvite ? "customer" : "customer");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [showPrivacyConsentModal, setShowPrivacyConsentModal] = useState(false);
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    mobile: "",
    email: storedStaffEmail, // Pre-fill email for staff invites
    password: "",
    businessName: "",
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [checkingPassword, setCheckingPassword] = useState(false);
  const [passwordBreached, setPasswordBreached] = useState(false);
  const [emailLocked, setEmailLocked] = useState(false);
  const [inviteBusinessName, setInviteBusinessName] = useState("");
  const [inviteRole, setInviteRole] = useState("");

  const { signIn, signUp, user, profile, refreshProfile } = useAuth();
  const { isStaff, hasAccess, loading: staffLoading } = useStaffAccess();
  const { toast } = useToast();
  
  // Rate limiting for login attempts (5 attempts per 15 minutes)
  const { checkRateLimit, recordAttempt, reset: resetRateLimit } = useRateLimit({
    maxAttempts: 5,
    windowMs: 15 * 60 * 1000, // 15 minutes
  });

  // For staff invites, fetch invitation details and set up the form
  // NOTE: step and userType are now initialized synchronously in useState
  useEffect(() => {
    const setupStaffInvite = async () => {
      if (!isStaffInvite || !inviteToken) return;
      
      // Store token in sessionStorage if we got it from URL
      if (urlInviteToken) {
        sessionStorage.setItem("pendingInviteToken", urlInviteToken);
      }
      
      // Fetch invitation preview to get email and lock it
      try {
        const { data: previewData, error: previewError } = await supabase
          .rpc("get_invitation_preview", {
            p_token: inviteToken,
          });

        if (!previewError && previewData && previewData.length > 0 && previewData[0].is_valid) {
          const inviteData = previewData[0];
          // Pre-fill and lock email
          setFormData(prev => ({ ...prev, email: inviteData.email }));
          setEmailLocked(true);
          setInviteBusinessName(inviteData.business_name);
          setInviteRole(inviteData.role);
          // Also store in sessionStorage
          sessionStorage.setItem("staffInviteEmail", inviteData.email);
        }
      } catch (err) {
        console.error("Error fetching invitation preview:", err);
        // Continue anyway - we'll validate on accept
      }
    };
    
    setupStaffInvite();
  }, [isStaffInvite, inviteToken, urlInviteToken]);

  useEffect(() => {
    // If not authenticated yet, nothing to do
    if (!user || !profile) return;
    
    // CRITICAL: Check for pending staff invite FIRST
    // This must happen IMMEDIATELY after registration, before staff access check completes
    const pendingToken = sessionStorage.getItem("pendingInviteToken");
    if (pendingToken) {
      navigate(`/accept-invite?token=${pendingToken}`);
      return;
    }

    // NOW wait for staff access check for proper routing
    if (staffLoading) return;
    
    // Priority: staff membership takes precedence over profile role
    // This ensures staff members (even with role="customer") land in dashboard
    if (isStaff || hasAccess) {
      navigate("/dashboard");
      return;
    }
    
    // Fall back to role-based routing for non-staff users
    if (profile.role === "customer") {
      navigate("/customer/bookings");
    } else if (profile.role === "solo" || profile.role === "center_admin") {
      navigate("/dashboard");
    } else {
      navigate("/");
    }
  }, [user, profile, navigate, isStaff, hasAccess, staffLoading]);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    try {
      emailSchema.parse(formData.email);
    } catch (e) {
      if (e instanceof z.ZodError) {
        newErrors.email = e.errors[0].message;
      }
    }

    try {
      passwordSchema.parse(formData.password);
    } catch (e) {
      if (e instanceof z.ZodError) {
        newErrors.password = e.errors[0].message;
      }
    }

    if (!isLogin && !formData.firstName.trim()) {
      newErrors.firstName = "First name is required";
    }

    if (!isLogin && !formData.lastName.trim()) {
      newErrors.lastName = "Last name is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Check password for breaches when it changes (for signup)
  useEffect(() => {
    if (isLogin || !formData.password || formData.password.length < 8) {
      setPasswordBreached(false);
      return;
    }

    const checkPassword = async () => {
      setCheckingPassword(true);
      try {
        const result = await checkPasswordBreached(formData.password);
        setPasswordBreached(result.breached);
        if (result.breached) {
          setErrors(prev => ({
            ...prev,
            password: `This password was found in ${result.count?.toLocaleString() || 'multiple'} data breaches. Please choose a different password.`
          }));
        } else {
          setErrors(prev => {
            const { password, ...rest } = prev;
            // Only remove if it was a breach error
            if (password?.includes('data breach')) return rest;
            return prev;
          });
        }
      } catch {
        // Fail open - don't block if check fails
        setPasswordBreached(false);
      } finally {
        setCheckingPassword(false);
      }
    };

    // Debounce the check
    const timeout = setTimeout(checkPassword, 500);
    return () => clearTimeout(timeout);
  }, [formData.password, isLogin]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!isLogin && step === 1 && userType) {
      setStep(2);
      return;
    }

    if (!validateForm()) return;

    // Block signup if password is breached
    if (!isLogin && passwordBreached) {
      toast({
        title: "Unsafe Password",
        description: "This password has been found in data breaches. Please choose a different password.",
        variant: "destructive",
      });
      return;
    }

    // For signup, show privacy consent modal first (APP-compliant)
    if (!isLogin) {
      setShowPrivacyConsentModal(true);
      return;
    }

    await performAuth();
  };

  const handlePrivacyConsentAccepted = (marketing: boolean) => {
    setMarketingConsent(marketing);
    setShowPrivacyConsentModal(false);
    // Now show terms modal
    setShowTermsModal(true);
  };

  const performAuth = async () => {
    // Check rate limit for login attempts
    if (isLogin) {
      const { allowed, waitTime } = checkRateLimit();
      if (!allowed) {
        toast({
          title: "Too many attempts",
          description: `Please wait ${waitTime} seconds before trying again.`,
          variant: "destructive",
        });
        return;
      }
      recordAttempt();
    }

    setIsLoading(true);

    try {
      if (isLogin) {
        const { error } = await signIn(formData.email, formData.password);
        if (error) {
          // Log failed attempt using secure RPC function (security definer)
          try {
            await supabase.rpc("record_login_attempt", {
              p_email: formData.email,
              p_ip_address: null,
              p_success: false,
            });
          } catch (e) {
            // Silently ignore logging errors
          }

          if (error.message.includes("Invalid login credentials")) {
            toast({
              title: "Login failed",
              description: "Invalid email or password. Please try again.",
              variant: "destructive",
            });
          } else {
            toast({
              title: "Login failed",
              description: error.message,
              variant: "destructive",
            });
          }
        } else {
          // Reset rate limit on successful login
          resetRateLimit();
          
          // Log successful attempt using secure RPC function
          try {
            await supabase.rpc("record_login_attempt", {
              p_email: formData.email,
              p_ip_address: null,
              p_success: true,
            });
          } catch (e) {
            // Silently ignore logging errors
          }

          toast({
            title: "Welcome back!",
            description: "You have successfully signed in.",
          });
          // Ensure the profile exists/loads; routing will happen via the useEffect above
          await refreshProfile();
        }
      } else {
        const fullName = `${formData.firstName} ${formData.lastName}`.trim();
        const formattedPhone = formData.mobile ? (formData.mobile.startsWith("+61") ? formData.mobile : `+61${formData.mobile.replace(/\D/g, "")}`) : undefined;
        const { error } = await signUp(formData.email, formData.password, fullName, userType, formattedPhone);
        if (error) {
          if (error.message.includes("already registered")) {
            toast({
              title: "Account exists",
              description: "This email is already registered. Please sign in instead.",
              variant: "destructive",
            });
          } else {
            toast({
              title: "Registration failed",
              description: error.message,
              variant: "destructive",
            });
          }
        } else {
          // Check if there's a pending staff invite - redirect there instead
          const pendingToken = sessionStorage.getItem("pendingInviteToken");
          if (pendingToken && isStaffInvite) {
            toast({
              title: "Account created!",
              description: "Now accepting your team invitation...",
            });
            // For staff invites, redirect to accept invite page
            // The auth state change will handle the navigation
          } else {
            toast({
              title: "Account created!",
              description: "Please check your email to verify your account.",
            });
            // Redirect to verification pending page
            navigate("/verify-email-pending");
          }
        }
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleTermsAccepted = async () => {
    setShowTermsModal(false);
    
    // Store consent info for logging after successful signup
    // We'll log these after authentication since we now require auth for audit inserts
    const consentInfo = {
      email: formData.email,
      userType: isStaffInvite ? "staff" : userType,
      marketingConsent: marketingConsent,
      userAgent: navigator.userAgent,
      platform: navigator.platform,
      language: navigator.language,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      screenWidth: window.screen.width,
      screenHeight: window.screen.height,
    };
    
    // Store in sessionStorage temporarily for post-signup logging
    sessionStorage.setItem("pendingConsentLog", JSON.stringify(consentInfo));

    await performAuth();
  };

  // Helper to preserve invite params when switching modes
  const getModeLink = (targetMode: string) => {
    const params = new URLSearchParams();
    params.set("mode", targetMode);
    if (inviteToken) {
      params.set("inviteToken", inviteToken);
      params.set("staffInvite", "true");
    }
    return `/auth?${params.toString()}`;
  };

  return (
    <div className="min-h-screen bg-background">
      <SEOHead
        title={isLogin ? "Sign In" : "Create Account"}
        description={isLogin 
          ? "Sign in to Wellness Connect PH to book wellness services or manage your business. Access your account securely."
          : "Create your free Wellness Connect PH account. Join as a customer, solo practitioner, or business owner."
        }
        keywords={isLogin 
          ? "wellness connect login, sign in, wellness booking account"
          : "wellness connect signup, create account, wellness business registration, practitioner signup"
        }
        canonicalUrl="/auth"
        noIndex={true}
      />
      <Navbar />
      
      <main className="pt-24 pb-16 px-4">
        <div className="max-w-lg mx-auto">
          {/* Logo */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl gradient-primary mb-4 shadow-wellness-glow">
              <Leaf className="w-8 h-8 text-primary-foreground" />
            </div>
            <h1 className="font-display text-3xl font-bold text-foreground mb-2">
              {isLogin 
                ? "Welcome Back" 
                : isStaffInvite 
                  ? "Create Your Staff Account" 
                  : step === 1 
                    ? "Join Wellness Connect" 
                    : "Create Your Account"}
            </h1>
            <p className="text-muted-foreground">
              {isLogin 
                ? "Sign in to continue your wellness journey"
                : isStaffInvite
                  ? inviteBusinessName 
                    ? `Join ${inviteBusinessName} as ${inviteRole || "staff"}`
                    : "Set up your account to join the team"
                  : step === 1 
                    ? "Choose how you want to use Wellness Connect" 
                    : "Fill in your details to get started"
              }
            </p>
          </div>

          {/* Progress (signup only, not for staff invites) */}
          {!isLogin && !isStaffInvite && (
            <div className="flex items-center justify-center gap-2 mb-8">
              <div className={`w-3 h-3 rounded-full ${step >= 1 ? "gradient-primary" : "bg-muted"}`} />
              <div className={`w-12 h-1 rounded ${step >= 2 ? "gradient-primary" : "bg-muted"}`} />
              <div className={`w-3 h-3 rounded-full ${step >= 2 ? "gradient-primary" : "bg-muted"}`} />
            </div>
          )}

          <Card variant="elevated" className="p-8">
            <form onSubmit={handleSubmit}>
              {isLogin ? (
                <div className="space-y-6">
                  <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                      <Input
                        id="email"
                        type="email"
                        placeholder="you@example.com"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        className="pl-10"
                        required
                      />
                    </div>
                    {errors.email && <p className="text-sm text-destructive">{errors.email}</p>}
                  </div>

                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <Label htmlFor="password">Password</Label>
                      <Link to="/forgot-password" className="text-sm text-primary hover:underline">
                        Forgot password?
                      </Link>
                    </div>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                      <Input
                        id="password"
                        type={showPassword ? "text" : "password"}
                        placeholder="••••••••"
                        value={formData.password}
                        onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                        className="pl-10 pr-10"
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      >
                        {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                    {errors.password && <p className="text-sm text-destructive">{errors.password}</p>}
                  </div>

                  <Button variant="hero" type="submit" className="w-full" size="lg" disabled={isLoading}>
                    {isLoading && <Loader2 className="w-5 h-5 mr-2 animate-spin" />}
                    Sign In
                  </Button>
              </div>
              ) : (step === 1 && !isStaffInvite) ? (
                <div className="space-y-4">
                  {userTypes.map((type) => (
                    <button
                      key={type.id}
                      type="button"
                      onClick={() => setUserType(type.id)}
                      className={cn(
                        "w-full p-4 rounded-xl border-2 text-left transition-all",
                        userType === type.id
                          ? "border-primary bg-primary/5"
                          : "border-border hover:border-primary/50"
                      )}
                    >
                      <div className="flex items-start gap-4">
                        <div className={cn(
                          "w-12 h-12 rounded-xl flex items-center justify-center",
                          userType === type.id ? "gradient-primary" : "bg-secondary"
                        )}>
                          <type.icon className={cn(
                            "w-6 h-6",
                            userType === type.id ? "text-primary-foreground" : "text-muted-foreground"
                          )} />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center justify-between">
                            <h3 className="font-semibold text-foreground">{type.title}</h3>
                            {userType === type.id && (
                              <div className="w-5 h-5 rounded-full gradient-primary flex items-center justify-center">
                                <Check className="w-3 h-3 text-primary-foreground" />
                              </div>
                            )}
                          </div>
                          <p className="text-sm text-muted-foreground mt-1">{type.description}</p>
                          <p className="text-sm font-medium text-primary mt-2">{type.price}</p>
                        </div>
                      </div>
                    </button>
                  ))}

                  <Button
                    variant="hero"
                    type="submit"
                    className="w-full mt-6"
                    size="lg"
                    disabled={!userType}
                  >
                    Continue
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </div>
              ) : (
                <div className="space-y-6">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="firstName">First Name</Label>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                        <Input
                          id="firstName"
                          placeholder="John"
                          value={formData.firstName}
                          onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                          className="pl-10"
                          required
                        />
                      </div>
                      {errors.firstName && <p className="text-sm text-destructive">{errors.firstName}</p>}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="lastName">Last Name</Label>
                      <Input
                        id="lastName"
                        placeholder="Doe"
                        value={formData.lastName}
                        onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                        required
                      />
                      {errors.lastName && <p className="text-sm text-destructive">{errors.lastName}</p>}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="mobile">Mobile #</Label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">+61</span>
                      <Input
                        id="mobile"
                        placeholder="4XX XXX XXX"
                        value={formData.mobile}
                        onChange={(e) => {
                          // Only allow digits and limit to 9
                          const digitsOnly = e.target.value.replace(/\D/g, "").slice(0, 9);
                          setFormData({ ...formData, mobile: digitsOnly });
                        }}
                        className="pl-12"
                        maxLength={9}
                      />
                    </div>
                    <p className="text-xs text-muted-foreground">Enter 9 digits (e.g., 481739592)</p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                      <Input
                        id="email"
                        type="email"
                        placeholder="you@example.com"
                        value={formData.email}
                        onChange={(e) => !emailLocked && setFormData({ ...formData, email: e.target.value })}
                        className={cn("pl-10", emailLocked && "bg-muted cursor-not-allowed")}
                        required
                        disabled={emailLocked}
                      />
                    </div>
                    {emailLocked && (
                      <p className="text-xs text-muted-foreground">
                        Email is locked to match your invitation
                      </p>
                    )}
                    {errors.email && <p className="text-sm text-destructive">{errors.email}</p>}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="password">Password</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                      <Input
                        id="password"
                        type={showPassword ? "text" : "password"}
                        placeholder="••••••••"
                        value={formData.password}
                        onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                        className="pl-10 pr-10"
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      >
                        {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                    {/* Real-time password strength indicator */}
                    <PasswordStrengthIndicator password={formData.password} />
                    {errors.password && <p className="text-sm text-destructive mt-2">{errors.password}</p>}
                  </div>

                  {isStaffInvite ? (
                    /* Staff invite: single full-width button, no back option */
                    <Button 
                      variant="hero" 
                      type="submit" 
                      className="w-full" 
                      size="lg" 
                      disabled={isLoading || checkingPassword || !isPasswordValid(formData.password) || passwordBreached}
                    >
                      {isLoading && <Loader2 className="w-5 h-5 mr-2 animate-spin" />}
                      Create Account
                    </Button>
                  ) : (
                    <div className="flex gap-4">
                      <Button
                        variant="outline"
                        type="button"
                        onClick={() => setStep(1)}
                        className="flex-1"
                        size="lg"
                      >
                        Back
                      </Button>
                      <Button 
                        variant="hero" 
                        type="submit" 
                        className="flex-1" 
                        size="lg" 
                        disabled={isLoading || checkingPassword || !isPasswordValid(formData.password) || passwordBreached}
                      >
                        {isLoading && <Loader2 className="w-5 h-5 mr-2 animate-spin" />}
                        Create Account
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </form>

            <p className="mt-8 text-center text-sm text-muted-foreground">
              {isLogin ? "Don't have an account? " : "Already have an account? "}
              <Link 
                to={getModeLink(isLogin ? "register" : "login")} 
                className="text-primary font-medium hover:underline"
              >
                {isLogin ? "Sign up" : "Sign in"}
              </Link>
            </p>
          </Card>
        </div>
      </main>

      {/* Privacy Consent Modal (APP-compliant) */}
      <PrivacyConsentModal
        open={showPrivacyConsentModal}
        onOpenChange={setShowPrivacyConsentModal}
        onAccept={handlePrivacyConsentAccepted}
        isSubmitting={isLoading}
      />

      {/* Terms Modal */}
      <TermsModal
        isOpen={showTermsModal}
        onClose={() => setShowTermsModal(false)}
        onAccept={handleTermsAccepted}
        userType={isStaffInvite ? "staff" : userType}
        email={formData.email}
      />
    </div>
  );
}
